import { useEffect, useRef } from 'react';
import { loadTwitchApi, type TwitchPlayer as TwitchPlayerApi } from '../lib/loadScript';
import type { PlaybackState } from '../lib/types';
import type { Source } from '../lib/parseUrl';

interface Props {
  source: Extract<Source, { provider: 'twitch' }>;
  muted: boolean;
  onState: (s: PlaybackState) => void;
}

/**
 * 公式 Twitch Embed (Interactive Frames) のみを使う。
 *
 * YouTubePlayer と同じライフサイクル方針。特に effect の依存を
 * 「どのチャンネル/VOD か」だけに絞ることが重要で、コールバックや source
 * オブジェクトを依存に入れると、状態が変わるたびに配信が再読み込みされる。
 *
 * 音量と再生/停止は純正 UI に任せる。
 */
export function TwitchPlayer({ source, muted, onState }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<TwitchPlayerApi | null>(null);

  const onStateRef = useRef(onState);
  onStateRef.current = onState;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const sourceRef = useRef(source);
  sourceRef.current = source;

  const key = source.kind === 'channel' ? `c:${source.channel}` : `v:${source.videoId}`;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;

    // Twitch.Player は渡した要素の中に iframe を生やす。React が触らない
    // 専用の子要素を effect 側で用意して所有権を分ける。
    const host = document.createElement('div');
    host.className = 'twitch-host';
    container.replaceChildren(host);

    loadTwitchApi()
      .then((Player) => {
        if (disposed) return;
        const current = sourceRef.current;
        const player = new Player(host, {
          ...(current.kind === 'channel'
            ? { channel: current.channel }
            : { video: `v${current.videoId}` }),
          parent: [window.location.hostname],
          width: '100%',
          height: '100%',
          autoplay: false,
          muted: true,
          playsinline: true,
        });
        playerRef.current = player;
        player.addEventListener(Player.READY, () => {
          if (disposed) return;
          safely(() => player.setMuted(mutedRef.current));
        });
        player.addEventListener(Player.PLAY, () => onStateRef.current('playing'));
        player.addEventListener(Player.PAUSE, () => onStateRef.current('paused'));
        player.addEventListener(Player.ENDED, () => onStateRef.current('ended'));
      })
      .catch(() => onStateRef.current('error'));

    return () => {
      disposed = true;
      safely(() => playerRef.current?.destroy?.());
      playerRef.current = null;
      container.replaceChildren();
    };
  }, [key]);

  useEffect(() => {
    const p = playerRef.current;
    if (p) safely(() => p.setMuted(muted));
  }, [muted]);

  return (
    <div className="player-frame">
      <div ref={containerRef} className="player-host" />
    </div>
  );
}

function safely(fn: () => void): void {
  try {
    fn();
  } catch {
    /* 未準備・破棄済みは無視 */
  }
}
